import {
  CaseReducerActions,
  createSlice,
  PayloadAction,
  Reducer,
  Slice,
} from '@reduxjs/toolkit';

import { PaginationDefaults } from '../../../../shared/utils/pagination-defaults.util';
import { PlatformModel } from '../../../shared/models/platform.model';
import { getApkPlatforms } from '../../components/excepted-apps-release-form/thunks/get-apk-platforms.thunk';
import { ReleaseModel } from '../../models/release.model';
import { ReleasesSliceReducers } from '../../models/slices/releases-reducers.model';
import { ReleasesStateModel } from '../../models/slices/releases-state.model';
import { createAppRelease } from '../../thunks/create-app-release.thunk';
import { deleteAppRelease } from '../../thunks/delete-app-release.thunk';
import { editAppRelease } from '../../thunks/edit-app-release.thunk';
import { getAppReleasesTotalCount } from '../../thunks/get-app-releases-total-count.thunk';
import { getAppReleases } from '../../thunks/get-app-releases.thunk';

const releasesInitialState: ReleasesStateModel = {
  releases: null,
  hasError: false,
  pagination: {
    totalCount: PaginationDefaults.totalCount,
    pageNumber: PaginationDefaults.pageNumber,
    size: PaginationDefaults.size,
    offset: PaginationDefaults.offset,
  },
  platforms: [],
  apkInfoValues: null,
};

export const releasesSlice: Slice<ReleasesStateModel, ReleasesSliceReducers> =
  createSlice({
    name: 'releases',
    initialState: releasesInitialState,
    reducers: {
      resetReleasesSliceState(state: ReleasesStateModel) {
        Object.assign(state, releasesInitialState);
      },

      setAppId(state, action) {
        state.appId = action.payload;
      },

      addPagination(state, action) {
        state.pagination.size = action.payload.size;
        state.pagination.offset = action.payload.offset;
        state.pagination.pageNumber = action.payload.pageNumber;
      },

      resetPagination(state, action) {
        state.pagination.offset = PaginationDefaults.offset;
        state.pagination.size = action.payload.size;
        state.pagination.pageNumber = PaginationDefaults.pageNumber;
      },

      setHasReleasesFormError(state, action) {
        state.hasError = action.payload;
      },

      setApkInfoValues(state, action) {
        state.apkInfoValues = action.payload;
      },
    },
    extraReducers: builder => {
      builder.addCase(
        getAppReleases.fulfilled,
        (state: ReleasesStateModel, action: PayloadAction<ReleaseModel[]>) => {
          state.releases = action.payload;
        }
      );
      builder.addCase(
        getAppReleasesTotalCount.fulfilled,
        (state: ReleasesStateModel, action: PayloadAction<number>) => {
          state.pagination.totalCount = action.payload;
        }
      );
      builder.addCase(editAppRelease.rejected, (state: ReleasesStateModel) => {
        state.hasError = true;
      });
      builder.addCase(
        editAppRelease.fulfilled,
        (state: ReleasesStateModel, action: PayloadAction<ReleaseModel>) => {
          if (state.releases) {
            const indexOfRelease = state.releases.findIndex(
              item => item.id === action.payload.id
            );
            state.releases.splice(indexOfRelease, 1, action.payload);
          }
        }
      );
      builder.addCase(
        createAppRelease.fulfilled,
        (state: ReleasesStateModel, action: PayloadAction<ReleaseModel>) => {
          state.releases = [...(state.releases ?? []), action.payload];
        }
      );
      builder.addCase(
        deleteAppRelease.fulfilled,
        (state: ReleasesStateModel, action: PayloadAction<ReleaseModel>) => {
          if (state.releases) {
            state.releases = state.releases.filter(
              release => release.id !== action.payload.id
            );
          }
        }
      );
      builder.addCase(
        getApkPlatforms.fulfilled,
        (state, action: PayloadAction<PlatformModel[]>) => {
          state.platforms = action.payload;
        }
      );
    },
  });

export const releasesActions: CaseReducerActions<ReleasesSliceReducers> =
  releasesSlice.actions;

const releasesSliceReducers: Reducer<ReleasesStateModel> =
  releasesSlice.reducer;

export default releasesSliceReducers;
